import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  BookOpen, CheckCircle, XCircle, AlertTriangle, RefreshCw, 
  Search, User, FileText, ChevronDown, ChevronUp, RefreshCcw
} from 'lucide-react';
import { format } from 'date-fns';

export default function TrainingComplianceDashboard() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // all, compliant, non-compliant

  useEffect(() => {
    fetchTrainingCompliance();
  }, []);

  const fetchTrainingCompliance = async () => {
    setLoading(true);
    try {
      const res = await api.get('/operations/training-compliance');
      setData(res.data);
    } catch (error) {
      console.error('Training compliance fetch error:', error);
      toast.error('Failed to load training compliance data');
    } finally {
      setLoading(false);
    }
  };

  const filteredStaff = data?.staff_compliance?.filter(person => {
    const matchesSearch = person.user_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = 
      filterStatus === 'all' ||
      (filterStatus === 'compliant' && person.compliance_percent === 100) ||
      (filterStatus === 'non-compliant' && person.compliance_percent < 100);
    return matchesSearch && matchesFilter;
  }) || [];

  const getComplianceBadge = (percent) => {
    if (percent === 100) {
      return <Badge className="bg-emerald-100 text-emerald-700">Fully Compliant</Badge>;
    } else if (percent >= 75) {
      return <Badge className="bg-amber-100 text-amber-700">Partially Compliant</Badge>;
    } else {
      return <Badge className="bg-red-100 text-red-700">Non-Compliant</Badge>;
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      employee: 'bg-blue-100 text-blue-700',
      physician: 'bg-purple-100 text-purple-700',
      admin: 'bg-slate-100 text-slate-700'
    };
    return <Badge className={colors[role] || colors.employee}>{role}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="training-compliance-dashboard">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Training Compliance</h1>
          <p className="text-slate-500">Track training completion status by employee</p>
        </div>
        <Button onClick={fetchTrainingCompliance} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Total Staff</p>
                <p className="text-2xl font-bold text-slate-900">{data?.total_staff || 0}</p>
              </div>
              <User className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Fully Compliant</p>
                <p className="text-2xl font-bold text-emerald-600">{data?.fully_compliant || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Needs Attention</p>
                <p className="text-2xl font-bold text-red-600">{data?.needs_attention || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Training Docs</p>
                <p className="text-2xl font-bold text-purple-600">{data?.total_training_docs || 0}</p>
              </div>
              <BookOpen className="h-8 w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <CardTitle className="text-lg">Staff Training Status</CardTitle>
            <div className="flex gap-4 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search staff..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-staff"
                />
              </div>
              <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
                <Button
                  variant={filterStatus === 'all' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterStatus('all')}
                >
                  All
                </Button>
                <Button
                  variant={filterStatus === 'compliant' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterStatus('compliant')}
                  className="text-emerald-600"
                >
                  Compliant
                </Button>
                <Button
                  variant={filterStatus === 'non-compliant' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterStatus('non-compliant')}
                  className="text-red-600"
                >
                  Overdue
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredStaff.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No staff found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredStaff.map((person) => (
                <div 
                  key={person.user_id} 
                  className="border rounded-lg overflow-hidden"
                  data-testid={`staff-row-${person.user_id}`}
                >
                  {/* Person Header */}
                  <div 
                    className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      person.compliance_percent === 100 ? 'bg-emerald-50/50' : ''
                    }`}
                    onClick={() => setExpandedUser(
                      expandedUser === person.user_id ? null : person.user_id
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                          <User className="h-5 w-5 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{person.user_name}</p>
                          <div className="flex gap-2 mt-1">
                            {getRoleBadge(person.role)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {getComplianceBadge(person.compliance_percent)}
                        <div className="w-32">
                          <Progress 
                            value={person.compliance_percent} 
                            className={`h-2 ${
                              person.compliance_percent === 100 ? 'bg-emerald-100' : 
                              person.compliance_percent >= 75 ? 'bg-amber-100' : 'bg-red-100'
                            }`}
                          />
                          <p className="text-xs text-center mt-1">{person.compliance_percent}% complete</p>
                        </div>
                        {expandedUser === person.user_id ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Training Details */}
                  {expandedUser === person.user_id && (
                    <div className="border-t p-4 bg-slate-50">
                      <h4 className="text-sm font-medium text-slate-700 mb-3">Training Status</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {person.training_status?.map((training) => (
                          <div 
                            key={training.training_id}
                            className={`p-3 rounded border ${
                              !training.is_required ? 'bg-slate-100 border-slate-200' :
                              training.completed && !training.needs_recertification ? 'bg-emerald-50 border-emerald-200' :
                              training.needs_recertification ? 'bg-amber-50 border-amber-200' :
                              'bg-red-50 border-red-200'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="text-sm font-medium">{training.training_title}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  {training.is_mandatory && (
                                    <Badge variant="outline" className="text-xs">Required</Badge>
                                  )}
                                  {!training.is_required && (
                                    <Badge variant="outline" className="text-xs bg-slate-100">Optional</Badge>
                                  )}
                                </div>
                              </div>
                              <div className="ml-2">
                                {training.completed && !training.needs_recertification ? (
                                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                                ) : training.needs_recertification ? (
                                  <RefreshCcw className="h-5 w-5 text-amber-500" />
                                ) : training.is_required ? (
                                  <XCircle className="h-5 w-5 text-red-500" />
                                ) : (
                                  <div className="h-5 w-5" />
                                )}
                              </div>
                            </div>
                            {training.completed && (
                              <p className="text-xs text-slate-500 mt-2">
                                Completed: {training.completed_at ? format(new Date(training.completed_at), 'MMM d, yyyy') : 'Unknown'}
                                {training.version_completed && ` (v${training.version_completed})`}
                              </p>
                            )}
                            {training.needs_recertification && (
                              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                New version available (v{training.current_version})
                              </p>
                            )}
                            {!training.completed && training.is_required && (
                              <p className="text-xs text-red-600 mt-2">Not completed</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
